import { UtilService } from 'src/app/shared/util.service';
import { Component, OnInit, Input, Output, EventEmitter, OnDestroy, OnChanges, HostListener } from '@angular/core';
import { Hotel } from 'app/shared/model';
import { Subscription } from 'rxjs';
import { Select, Store } from '@ngxs/store';
import { Observable } from 'rxjs';
import { CustomersState } from 'app/states/customers/customers.state';

export class FilterValue {
  selected: any = null;
  selectedMultiple: string[] = [];
  values: string[] = [];
}

export class Filters {
  all = new FilterValue();
  brands = new FilterValue();
  regions = new FilterValue();
}

@Component({
    selector: 'mh-core-lib-filter-chain-zone',
    templateUrl: './chain-zone.component.html',
    styleUrls: ['./chain-zone.component.scss'],
    standalone: false
})
export class CoreLibChainZoneComponent implements OnInit, OnDestroy, OnChanges {
  @Select(CustomersState.relativeCurrentChain) customersReady$: Observable<any[]>;

  @Input()
  productId;

  chain: Hotel[] = [];

  @Input()
  disabled = false;

  @Input()
  multipleSelectionMode = false;

  filters: any = new Filters();
  isOpen: { [key: string]: boolean } = { all: false, brands: false, regions: false };
  searchTerms: { [key: string]: string } = { brands: '', regions: '' };

  allSelected: { [key: string]: boolean } = { brands: false, regions: false };
  filteredValues: { [key: string]: string[] } = { brands: [], regions: [] };

  @Output()
  chainChanged: EventEmitter<Hotel[]> = new EventEmitter();

  customerSubscribe: Subscription;
  chainSubscribe: Subscription;
  formatedChain: any[];

  /** Hoteles con que se armó el menú; evita rehacerlo cuando la cadena no cambió. */
  private chainKey = '';

  constructor(private utilService: UtilService, private store: Store) {
    this.customerSubscribe = this.utilService.isCustomerChanged().subscribe(() => {
      this.init();
    });
  }

  ngOnDestroy() {
    this.customerSubscribe.unsubscribe();
    this.chainSubscribe?.unsubscribe();
  }

  ngOnInit() {
    this.filteredValues = { brands: [], regions: [] };
    this.allSelected = { brands: false, regions: false };
    this.init();

    // `isCustomerChanged` emite antes de que `SessionService.storeCustomers()` traiga la
    // cadena nueva por HTTP, así que el init de arriba se arma con la anterior. Al cambiar
    // a un hotel de otra cadena hay que rehacer marcas y regiones cuando el store ya la
    // tiene: si no, el menú queda ofreciendo las del hotel previo. Se suscribe después del
    // primer init y compara los hoteles, así la emisión inmediata del selector no lo repite.
    this.chainSubscribe = this.customersReady$.subscribe((chain) => {
      if (this.keyOf(chain) === this.chainKey) return;

      this.init();
    });
  }

  /** Los hoteles de una cadena como conjunto: no depende del orden en que vengan. */
  private keyOf(chain: { id?: number | string }[]): string {
    return (chain || [])
      .map((customer) => customer?.id)
      .sort()
      .join(',');
  }

  ngOnChanges() {
    if (this.filters && this.filters.all) {
      this.reinitializeSecondLevelFilters();
      this.updateComputedProperties();
    }
  }

  @HostListener('document:click', ['$event'])
  onClick(event) {
    const clickResult = this.checkClickInside(event.target);
    if (!clickResult) {
      this.closeAllDropdowns();
    }
  }

  checkClickInside(target): boolean {
    if (
      target.id?.includes('chainSelectorTrigger-') ||
      target.id?.includes('chainSelectorMenu-') ||
      target.className?.includes('mat-radio') ||
      this.isChildOfElementWithIdPattern(target, 'chainSelectorTrigger-') ||
      this.isChildOfElementWithIdPattern(target, 'chainSelectorMenu-')
    ) {
      return true;
    } else if (target.parentNode) {
      return this.checkClickInside(target.parentNode);
    } else {
      return false;
    }
  }

  isChildOfElementWithIdPattern(element, idPattern) {
    let parent = element.parentNode;
    while (parent) {
      if (parent.id && parent.id.includes(idPattern)) {
        return true;
      }
      parent = parent.parentNode;
    }
    return false;
  }

  toggleDropdown(type: string) {
    if (this.disabled) return;

    Object.keys(this.isOpen).forEach((key) => {
      if (key !== type) {
        this.isOpen[key] = false;
        if (this.searchTerms[key]) {
          this.searchTerms[key] = '';
        }
      }
    });

    this.isOpen[type] = !this.isOpen[type];

    if (!this.isOpen[type] && this.searchTerms[type]) {
      this.searchTerms[type] = '';
    }
  }

  closeAllDropdowns() {
    Object.keys(this.isOpen).forEach((key) => {
      this.isOpen[key] = false;
      if (this.searchTerms[key]) {
        this.searchTerms[key] = '';
      }
    });
  }

  selectOption(type: string, value: string) {
    if (type === 'all') {
      this.filters[type].selected = value;
      this.reinitializeSecondLevelFilters();
      this.chainResult();
      this.isOpen[type] = false;
    } else if (this.multipleSelectionMode && (type === 'brands' || type === 'regions')) {
      this.selectMultipleOption(type, value);
    } else {
      this.filters[type].selected = value;
      this.chainResult();
      this.isOpen[type] = false;
    }
  }

  reinitializeSecondLevelFilters() {
    const selectedFilterType = this.filters.all.selected;
    if (selectedFilterType === 'brands' || selectedFilterType === 'regions') {
      this.initializeFilterValues(selectedFilterType);
    }
  }

  selectMultipleOption(type: string, value: string) {
    const selectedArray = this.filters[type].selectedMultiple;
    const index = selectedArray.indexOf(value);

    if (index > -1) {
      selectedArray.splice(index, 1);
    } else {
      selectedArray.push(value);
    }

    this.updateComputedProperties();
    this.chainResult();
  }

  isOptionSelected(type: string, value: string): boolean {
    if (type === 'all') {
      return this.filters[type].selected === value;
    } else if (this.multipleSelectionMode && (type === 'brands' || type === 'regions')) {
      return this.filters[type].selectedMultiple.includes(value);
    } else {
      return this.filters[type].selected === value;
    }
  }

  selectAll(type: string) {
    this.filters[type].selectedMultiple = [...this.filters[type].values];
    this.updateComputedProperties();
    this.chainResult();
  }

  deselectAll(type: string) {
    this.filters[type].selectedMultiple = [];
    this.updateComputedProperties();
    this.chainResult();
  }

  onSearchChange(type: string, event: any) {
    this.searchTerms[type] = event.target.value;
    this.updateFilteredValues(type);
  }

  private updateComputedProperties() {
    ['brands', 'regions'].forEach((key) => {
      this.allSelected[key] = this.filters[key].selectedMultiple.length === this.filters[key].values.length;
    });

    this.updateAllFilteredValues();
  }

  private updateFilteredValues(type: string) {
    const searchTerm = this.searchTerms[type]?.toLowerCase() || '';
    if (!searchTerm) {
      this.filteredValues[type] = [...this.filters[type].values];
    } else {
      this.filteredValues[type] = this.filters[type].values.filter((value: string) => {
        return value.toLowerCase().includes(searchTerm);
      });
    }
  }

  private updateAllFilteredValues() {
    ['brands', 'regions'].forEach((key) => {
      this.updateFilteredValues(key);
    });
  }

  init() {
    let chain = this.store.selectSnapshot(CustomersState.relativeCurrentChain);
    this.chainKey = this.keyOf(chain);
    chain = chain.map((customer) => {
      return {
        ...customer,
        chain_brand: customer.chain_property.brand,
        chain_region: customer.chain_property.region,
      };
    });
    this.formatedChain = chain;

    const brands = {};
    const regions = {};

    chain.forEach((customer) => {
      brands[customer.chain_brand] = customer.chain_brand;
      regions[customer.chain_region] = customer.chain_region;
    });

    this.filters.all.values = ['all'];

    this.filters.brands.values = Object.values(brands);
    this.filters.regions.values = Object.values(regions);

    ['regions', 'brands'].forEach((key) => {
      this.checkSizes(key);
    });

    this.checkSelected();
    this.updateComputedProperties();
    this.chainResult();
  }

  checkSizes(key) {
    if (!this.filters[key].values.includes('') || this.filters[key].values.length > 1) {
      this.filters.all.values = this.filters.all.values.concat([key]);
    }
  }

  checkSelected() {
    this.filters.all.selected = this.filters.all.values.length > 0 ? this.filters.all.values[0] : 'all';

    this.initializeFilterValues('brands');
    this.initializeFilterValues('regions');
  }

  initializeFilterValues(key: string) {
    if (this.filters[key].values.length > 0) {
      this.filters[key].selected = this.filters[key].values[0];

      if (this.multipleSelectionMode) {
        this.filters[key].selectedMultiple = [...this.filters[key].values];
      } else {
        this.filters[key].selectedMultiple = [this.filters[key].values[0]];
      }
    }
  }

  chainResult() {
    let customers: Hotel[] = [];
    let chain = this.store.selectSnapshot(CustomersState.relativeCurrentChain);

    chain = chain.map((customer) => {
      return {
        ...customer,
        chain_brand: customer.chain_property.brand,
        chain_region: customer.chain_property.region,
      };
    });

    switch (this.filters.all.selected) {
      case 'all':
        customers = chain;
        break;
      case 'brands':
        if (this.multipleSelectionMode) {
          customers = this.getMultipleFilterResults(chain, 'brands');
        } else {
          customers = chain.filter((x) => x.chain_brand === this.filters.brands.selected);
        }
        break;
      case 'regions':
        if (this.multipleSelectionMode) {
          customers = this.getMultipleFilterResults(chain, 'regions');
        } else {
          customers = chain.filter((x) => x.chain_region === this.filters.regions.selected);
        }
        break;
    }

    this.chainChanged.emit(customers);
  }

  private getMultipleFilterResults(chain: any[], filterType: string): Hotel[] {
    let customers: Hotel[] = [];

    if (filterType === 'brands' && this.filters.brands.selectedMultiple.length > 0) {
      customers = chain.filter((x) => this.filters.brands.selectedMultiple.includes(x.chain_brand));
    } else if (filterType === 'regions' && this.filters.regions.selectedMultiple.length > 0) {
      customers = chain.filter((x) => this.filters.regions.selectedMultiple.includes(x.chain_region));
    } else {
      customers = chain;
    }

    return customers;
  }
}
